import axios from 'axios'

import i18n from '../i18n'

/**
 * Shared axios instance.
 *
 * The default timeout is deliberately long: submitting a recording waits on
 * Azure inside the request, and that call crosses a tunnel. It matches the
 * proxy_read_timeout in nginx/nginx.conf, so the browser and the proxy give
 * up at roughly the same moment instead of the browser cutting a request the
 * server is still working on.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

const ACCESS_KEY = 'shadowtooti.access'
const REFRESH_KEY = 'shadowtooti.refresh'

export const tokenStore = {
  get access() {
    return localStorage.getItem(ACCESS_KEY)
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY)
  },
  set({ accessToken, refreshToken }) {
    localStorage.setItem(ACCESS_KEY, accessToken)
    localStorage.setItem(REFRESH_KEY, refreshToken)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 120_000,
  headers: { Accept: 'application/json' },
})

api.interceptors.request.use((config) => {
  const token = tokenStore.access
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

/**
 * In-flight refresh, shared by every request that hits a 401 at once.
 *
 * Without this, a dashboard that fires four requests in parallel would run
 * four refreshes. The first would succeed and the other three would present
 * a token that rotation has already revoked, logging the user out for no
 * reason.
 */
let refreshPromise = null

const listeners = new Set()

/** Subscribe to forced logouts, so the auth store can clear its state. */
export function onAuthExpired(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const subscriptionListeners = new Set()

/**
 * Subscribe to the access gate's refusal (tooti#121): once the server holds
 * a learner out — trial over, nothing paid for — every guarded call answers
 * 403 `subscription_required`, and the app sends them to the purchase page.
 */
export function onSubscriptionRequired(listener) {
  subscriptionListeners.add(listener)
  return () => subscriptionListeners.delete(listener)
}

function notifyExpired() {
  tokenStore.clear()
  listeners.forEach((listener) => listener())
}

async function refreshTokens() {
  const refreshToken = tokenStore.refresh
  if (!refreshToken) {
    throw new Error('No refresh token')
  }
  // A bare axios call, not `api`: going through the instance would recurse
  // back into this interceptor if the refresh itself returns 401.
  const { data } = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken })
  tokenStore.set(data)
  return data.accessToken
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error

    if (response?.status === 403 && response.data?.error === 'subscription_required') {
      subscriptionListeners.forEach((listener) => listener())
      return Promise.reject(error)
    }

    const isAuthCall = config?.url?.includes('/auth/')
    if (response?.status !== 401 || isAuthCall || config?._retried) {
      return Promise.reject(error)
    }

    config._retried = true

    try {
      refreshPromise = refreshPromise ?? refreshTokens()
      const access = await refreshPromise
      config.headers.Authorization = `Bearer ${access}`
      return api(config)
    } catch (refreshError) {
      notifyExpired()
      return Promise.reject(refreshError)
    } finally {
      refreshPromise = null
    }
  },
)

/**
 * Every error response on this API is one shape: `ApiErrorDto`
 * (`{ error, message, retryable }` — see backend-nest/src/common/dto/error.dto.ts).
 * `error` is the stable code to branch on (e.g. `audio_rejected`, `azure_timeout`);
 * `message` is only for display and may change wording at any time. The admin
 * panel (admin.js) talks to the same backend and reuses this same helper.
 */
export function errorCode(error) {
  return error?.response?.data?.error ?? null
}

export function isRetryable(error) {
  return error?.response?.data?.retryable === true
}

/** Pull a human-readable message out of an axios error. */
export function errorMessage(error, fallback = i18n.t('genericError', { ns: 'common' })) {
  const message = error?.response?.data?.message
  return typeof message === 'string' && message.length > 0 ? message : fallback
}

/**
 * Which of the app's three submission-failure screens applies (offline /
 * timeout / service). Lives with the practice-session reducer, which
 * classifies a failed submit the same way; re-exported here so the page
 * keeps importing it next to `errorMessage`.
 */
export { classifyFailure } from '../lib/practiceSession'
